import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { alert } from "@components/common/Alert";
import {
  DSButton,
  DSCheckbox,
  DSInput,
  DSLinkButton,
} from "@components/design-system/molecules";
import {
  faEye,
  faEyeSlash,
} from "@fortawesome/free-regular-svg-icons";
import {
  faArrowRight,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { faUserFriends } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { config, useTransition, animated } from "@react-spring/web";
import { captureException } from "@sentry/react";
import { isValidPhoneNumber } from "@src/utils/phoneUtils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link } from "react-router";

import { ContactType } from "./constants/ContactType";
import { EnterpriseBlockedModal } from "./EnterpriseBlockedModal";
import GetStartedContactInfo from "./GetStartedContactInfo";
import MobileSignupGate from "./MobileSignupGate";
import { useRedirectAuthenticatedUsers } from "./use-redirect-authenticated-users";
import OttoScribblesPeek from "../../assets/imgs/otto-scribbles-peek.png";
import { useAuth } from "../../hooks";
import Twemoji from "../../Twemoji";
import FullScreenLoading from "../common/loading/FullScreenLoading";

// Server message constants are inconsistently punctuated: some end in a
// period, some in "!" or "?", some in nothing at all.
const withTerminalPunctuation = (message) =>
  /[.!?]$/.test(message) ? message : `${message}.`;

export default function Register({
  teamInviteData = null,
  isTeamInvite = null,
  accessToken = null,
}) {
  const auth = useAuth();
  const { webSignupSalesLeads, releaseWebPayItFurwardReferrals } =
    useFlags();

  const [showPassword, setShowPassword] = useState(false);
  const [shouldShowOttoScribbles, setShouldShowOttoScribbles] =
    useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [blockedEmail, setBlockedEmail] = useState(null);
  const [showMobileEducation, setShowMobileEducation] = useState(
    () => {
      const ua = navigator.userAgent;
      const isIOS =
        (/iPhone|iPod|iPad/.test(ua) ||
          (navigator.platform === "MacIntel" &&
            navigator.maxTouchPoints > 1)) &&
        !window.MSStream;
      const isAndroid = /Android/i.test(ua);
      return isIOS || isAndroid;
    },
  );

  useRedirectAuthenticatedUsers("/welcome");

  //fire off GA event when user lands on register page
  useEffect(() => {
    window.gtag("event", "sign_up_visit");
    window.fbq("track", "ViewContent");
  }, []);

  function togglePassword() {
    setShowPassword(!showPassword);
  }

  const { loading } = auth;

  const popUpAnim = useTransition(shouldShowOttoScribbles, {
    from: { y: 0 },
    enter: { y: 0 },
    leave: { y: 40 },
    delay: 200,
    config: config.gentle,
  });

  const params = new URLSearchParams(window.location.search);
  const referralCode = params?.get("referralCode");
  const isOnboardingComplete = params?.has("OnboardingComplete");
  const role = params?.get("role");

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm({
    defaultValues: {
      email: teamInviteData?.unauthenticatedTeamInvite?.inviteeEmail,
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
      clinicName: "",
      salesDirectContact: false,
      contactType: ContactType.EMAIL,
      contactInfo:
        teamInviteData?.unauthenticatedTeamInvite?.inviteeEmail,
      address: "",
    },
  });

  useEffect(() => {
    if (teamInviteData?.unauthenticatedTeamInvite?.inviteeEmail) {
      reset({
        ...getValues(),
        email: teamInviteData.unauthenticatedTeamInvite.inviteeEmail,
        contactInfo:
          teamInviteData.unauthenticatedTeamInvite.inviteeEmail,
      });
    }
  }, [teamInviteData, reset, getValues]);

  const salesDirectContact = useWatch({
    control,
    name: "salesDirectContact",
  });
  const contactType = useWatch({ control, name: "contactType" });
  const email = useWatch({ control, name: "email" });

  function onError(errors) {
    console.log("RHF errors", errors);
    setErrorMessage("Please fix the highlighted fields.");
  }

  function onSubmit(data) {
    const {
      email,
      password,
      confirmPassword,
      firstName,
      lastName,
      clinicName,
      salesDirectContact,
      contactType,
      contactInfo,
      address,
      referralCode: formReferralCode,
    } = data;

    // Use form value if available, otherwise fall back to URL param
    // Normalize: trim, replace spaces with hyphens, convert digits to words, title-case
    const digitToWord = {
      2: "Two",
      3: "Three",
      4: "Four",
      5: "Five",
      6: "Six",
      7: "Seven",
      8: "Eight",
      9: "Nine",
      10: "Ten",
    };
    const rawReferralCode = formReferralCode || referralCode;
    const effectiveReferralCode = rawReferralCode
      ? rawReferralCode
          .trim()
          .replace(/[\s-]+/g, "-")
          .split("-")
          .map((s) => digitToWord[s] || s)
          .map(
            (s) =>
              s.charAt(0).toUpperCase() + s.slice(1).toLowerCase(),
          )
          .join("-")
      : rawReferralCode;

    setErrorMessage("");

    if (password !== confirmPassword) {
      setErrorMessage("Your passwords do not match");
      return;
    }

    // validate if MVP email includes @mvetpartners.com
    if (effectiveReferralCode?.includes("MVP")) {
      const emailDomain = email.split("@")[1];
      if (emailDomain !== "mvetpartners.com") {
        setErrorMessage(
          "Please use your @mvetpartners.com email address.",
        );

        return;
      }
    }

    // basic email validation
    if (!emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    var contactInfoSubmitted = null;

    if (webSignupSalesLeads) {
      if (clinicName.trim() === "") {
        setErrorMessage("Please enter your clinic name");
        return;
      }

      if (salesDirectContact) {
        if (contactType === ContactType.EMAIL) {
          const didPass = emailRegex.test(contactInfo ?? "");
          if (!didPass) {
            setErrorMessage(
              "Please enter a valid email so we can contact you",
            );
            return;
          }
        }

        if (
          contactType === ContactType.TEXT ||
          contactType === ContactType.CALL
        ) {
          if (!isValidPhoneNumber(contactInfo ?? "")) {
            setErrorMessage(
              "Enter a valid phone number like (555) 444-7890",
            );
            return;
          }
        }

        if (
          contactType === ContactType.SNAIL_MAIL ||
          contactType === ContactType.CARRIER_PIGEON
        ) {
          if (!address) {
            setErrorMessage(
              "Please enter a valid address so we can contact you",
            );
            return;
          }
        }

        contactInfoSubmitted = contactInfo;
      } else {
        contactInfoSubmitted = contactInfo;
      }
    }

    if (isTeamInvite) {
      auth
        .registerAsTeamMember({
          email: email,
          password: password,
          firstName: firstName,
          lastName: lastName,
          accessToken,
          clinicName,
          contactType,
          contactInfoSubmitted,
          address,
          role,
        })
        .catch((error) => {
          if (!CombinedGraphQLErrors.is(error)) {
            captureException(error);
          }

          const isEnterpriseBlocked = error?.errors?.some(
            (err) =>
              err.extensions?.code === "ENTERPRISE_DOMAIN_BLOCKED",
          );
          if (isEnterpriseBlocked) {
            setBlockedEmail(email);
            return;
          }

          const gqlErrors = error?.errors
            ?.map((err) => err.message)
            .join(", ");
          setErrorMessage(
            gqlErrors
              ? withTerminalPunctuation(gqlErrors)
              : "An unknown error occurred.",
          );
        });
    } else {
      auth
        .register(
          email,
          password,
          firstName,
          lastName,
          effectiveReferralCode,
          isOnboardingComplete,
          clinicName,
          contactType,
          contactInfoSubmitted,
          address,
          role,
        )
        .then(({ hasReferrer }) => {
          if (
            hasReferrer &&
            !isTeamInvite &&
            releaseWebPayItFurwardReferrals
          ) {
            alert(
              "success",
              "You've used a friend's Pay It Furward code! If you upgrade to Scribenote Pro, you and your friend will receive credits to help pets in need.",
            );
          }
        })
        .catch((error) => {
          if (!CombinedGraphQLErrors.is(error)) {
            captureException(error);
          }

          const isEnterpriseBlocked = error?.errors?.some(
            (err) =>
              err.extensions?.code === "ENTERPRISE_DOMAIN_BLOCKED",
          );
          if (isEnterpriseBlocked) {
            setBlockedEmail(email);
            return;
          }

          const gqlErrors = error?.errors
            ?.map((err) => err.message)
            .join(", ");
          setErrorMessage(
            gqlErrors
              ? withTerminalPunctuation(gqlErrors)
              : "An unknown error occurred.",
          );
        });
    }
  }

  if (showMobileEducation) {
    return (
      <MobileSignupGate
        onSkip={() => setShowMobileEducation(false)}
      />
    );
  }

  return (
    <>
      <div className="flex-1 flex flex-col md:flex-row md:items-start md:space-x-10 space-y-20 md:space-y-0 md:pt-20 lg:px-10 max-w-7xl md:mx-auto">
        <FullScreenLoading loadingTrigger={loading} />
        <div className="flex-1 flex flex-col items-center md:items-start space-y-8">
          <h2 className="text-center md:text-left text-3xl md:text-5xl pb-1 text-brand">
            Sign up for Scribenote
          </h2>
          {!isTeamInvite && (
            <Link to="/auth/login">
              <h4 className="font-medium text-brand underline">
                Already have an account? Sign In
              </h4>
            </Link>
          )}

          {referralCode &&
            !isTeamInvite &&
            releaseWebPayItFurwardReferrals && (
              <div className="bg-brand-subtle rounded-xl border border-brand-subtle p-4 space-y-4">
                <h5 className="font-medium">
                  <FontAwesomeIcon icon={faUserFriends} size="1x" />{" "}
                  You've used a friend's{" "}
                  <strong>Pay It Furward</strong> link!
                </h5>

                <h5 className="text-brand dark:text-white/50 text-sm">
                  This means that you and your friend will receive
                  credits when you upgrade to Pro! You can then redeem
                  these credits to help pets in need.
                </h5>
              </div>
            )}

          {isTeamInvite && (
            <div className="bg-indigo-500/20 rounded-xl border border-indigo-300 p-4 space-y-4">
              <h5 className="font-medium">
                You&apos;ll be joining Scribenote as part of this
                team:
              </h5>
              <div className="flex flex-row items-center space-x-2">
                <div className="rounded-full flex items-center justify-center h-12 w-12 bg-brand text-white">
                  <FontAwesomeIcon icon={faUsers} size="1x" />
                </div>
                <h4 className="text-lg">
                  {
                    teamInviteData?.unauthenticatedTeamInvite
                      ?.teamName
                  }
                </h4>
              </div>

              <h5 className="text-indigo-500 dark:text-white/50 text-sm">
                This means other members of this team will have access
                to your Scribenote data and you&apos;ll have access to
                theirs. Be sure you received this invitation from
                someone you trust.
              </h5>

              <div className="py-2">
                Already registered?
                <Link
                  to={`/teams/accept/${teamInviteData?.unauthenticatedTeamInvite?.uuid}?accessToken=${accessToken}`}
                  className="font-medium text-brand"
                >
                  &nbsp;Accept Here&nbsp;
                </Link>
              </div>
            </div>
          )}

          <div className="text-2xl mx-6 md:mx-0 flex flex-col space-y-4">
            <h4>
              You&apos;re 20 seconds away from totally automatic
              medical records <Twemoji emoji="🎉" />
            </h4>
            <ul className="list-disc pl-4 text-sm text-gray-600 dark:text-gray-300 space-y-2">
              <li>
                Loved and trusted by thousands of veterinarians and
                care teams.
              </li>
              <li>
                Get unlimited AI-powered notes just by signing up.
              </li>
              <li>
                Easy to get started with and customize with endless{" "}
                <DSLinkButton
                  href="https://scribenote.com/template-library"
                  target="_blank"
                  rel="noreferrer"
                >
                  templates
                </DSLinkButton>
                .
              </li>
              <li>
                14 days of free access to all Pro features like{" "}
                <DSLinkButton
                  href="https://scribenote.com/blog/introducing-scribephone-making-client-communication-a-walk-in-the-bark"
                  target="_blank"
                  rel="noreferrer"
                >
                  Scribephone
                </DSLinkButton>
                .
              </li>
              <li>
                <span className="rounded-minimal bg-cyan-400 text-white font-semibold text-xs px-xs py-xs">
                  New!
                </span>{" "}
                Meet our{" "}
                <DSLinkButton
                  href="https://scribenote.com/companion"
                  target="_blank"
                  rel="noreferrer"
                >
                  Companions
                </DSLinkButton>
                : 🐘 Summi, 🐢 Sage, and 🦔 Quill!
              </li>
            </ul>
          </div>
        </div>
        <div className="flex flex-1 flex-col items-center md:items-end justify-center space-y-4">
          <div className="flex justify-center relative">
            {popUpAnim(
              (styles, item) =>
                item && (
                  <animated.div
                    style={styles}
                    className="absolute z-0 -top-10"
                  >
                    <img src={OttoScribblesPeek} width="200px" />
                  </animated.div>
                ),
            )}
            <form
              onSubmit={handleSubmit(onSubmit, onError)}
              className="flex flex-col justify-center w-[300px] md:w-[400px] mx-auto z-10 p-10 rounded-xl shadow-md shadow-secondary bg-secondary"
              data-testid="registerForm"
            >
              <div className="space-y-5 pb-6 w-full">
                <div className="space-y-4 pb-5">
                  <div>
                    <div className="flex flex-row items-center space-x-4">
                      <DSInput
                        type="text"
                        placeholder="First Name"
                        dataTestId="firstName"
                        {...register("firstName", {
                          required: "Please enter a First Name",
                        })}
                      />
                      <DSInput
                        type="text"
                        placeholder="Last Name"
                        dataTestId="lastName"
                        {...register("lastName", {
                          required: "Please enter a Last Name",
                        })}
                      />
                    </div>
                    {errors.firstName && (
                      <p className="text-danger text-xs mt-1">
                        {errors.firstName.message}
                      </p>
                    )}
                    {errors.lastName && (
                      <p className="text-danger text-xs mt-1">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <DSInput
                      name="email"
                      type="email"
                      placeholder="Email Address"
                      disabled={isTeamInvite}
                      dataTestId="email"
                      {...register("email", {
                        required: "Please enter your email",
                        pattern: {
                          value: emailRegex,
                          message: "Please enter a valid email",
                        },
                      })}
                    />
                    {errors.email && (
                      <p className="text-danger text-xs mt-1">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="relative">
                  <DSInput
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    onFocus={() => setShouldShowOttoScribbles(false)}
                    onBlur={() => setShouldShowOttoScribbles(true)}
                    {...register("password", {
                      required: "Please enter a password",
                    })}
                  />
                  <div
                    onClick={() => togglePassword()}
                    className="group absolute -top-5 right-2 z-20 flex items-center space-x-1 cursor-pointer text-secondary"
                  >
                    <FontAwesomeIcon
                      icon={showPassword ? faEyeSlash : faEye}
                      size="sm"
                      className="group-hover:text-brand"
                    />
                    <h5 className="text-xs group-hover:text-brand">
                      {showPassword ? "Hide" : "Show"}
                    </h5>
                  </div>
                  {errors.password && (
                    <p className="text-danger text-xs mt-1">
                      {errors.password.message}
                    </p>
                  )}
                </div>
                <div>
                  <DSInput
                    name="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm password"
                    onFocus={() => setShouldShowOttoScribbles(false)}
                    onBlur={() => setShouldShowOttoScribbles(true)}
                    {...register("confirmPassword", {
                      required: "Please confirm your password",
                    })}
                  />
                  {errors.confirmPassword && (
                    <p className="text-danger text-xs mt-1">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                {!isTeamInvite && releaseWebPayItFurwardReferrals && (
                  <div className="relative">
                    <DSInput
                      type="text"
                      name="referralCode"
                      defaultValue={referralCode}
                      placeholder="Referral Code (Optional)"
                      required={false}
                      disabled={!!referralCode}
                      dataTestId="referralCode"
                      {...register("referralCode", {
                        required: false,
                      })}
                    />
                    {referralCode && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-gray-500 pointer-events-none">
                        Referral Code
                      </span>
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Make sure it&apos;s formatted like
                      Number-Adjective-Animal
                    </p>
                  </div>
                )}

                {webSignupSalesLeads ? (
                  <div className="space-y-4 pb-1">
                    <div>
                      <DSInput
                        type="text"
                        placeholder="Clinic Name"
                        dataTestId={"clinicName"}
                        {...register("clinicName", {
                          required: "Please enter your Clinic Name",
                        })}
                      />
                      {errors.clinicName && (
                        <p className="text-danger text-xs mt-1">
                          {errors.clinicName.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center mt-4">
                        <DSCheckbox
                          checked={salesDirectContact}
                          size="md"
                          dataTestId="salesDirectContact"
                          {...register("salesDirectContact")}
                        />

                        <div className="text-m leading-none ml-4">
                          I'm interested in an expert from Scribenote
                          helping me get started
                        </div>
                      </div>
                    </div>
                    <div>
                      {salesDirectContact && (
                        <GetStartedContactInfo
                          control={control}
                          register={register}
                          contactType={contactType}
                          userEmail={email}
                          setValue={setValue}
                        />
                      )}
                    </div>
                  </div>
                ) : null}
                {errorMessage && (
                  <p className="text-danger text-center text-xs">
                    {errorMessage}
                  </p>
                )}
                {blockedEmail && (
                  <EnterpriseBlockedModal
                    isOpen
                    onClose={() => setBlockedEmail(null)}
                    email={blockedEmail}
                  />
                )}
              </div>
              <DSButton
                type="submit"
                size="lg"
                dataTestId="register"
                variant="primary"
              >
                Register{" "}
                <FontAwesomeIcon
                  className="ml-2"
                  icon={faArrowRight}
                />
              </DSButton>

              <div
                className={"text-sm text-center pt-6 text-secondary"}
              >
                By clicking <span className={"italic"}>Register</span>
                , you are agreeing to our{" "}
                <DSLinkButton
                  href="https://www.scribenote.com/terms-of-service"
                  target="_blank"
                  rel="noreferrer"
                  dataTestId="terms-of-service-link"
                >
                  Terms of Service
                </DSLinkButton>{" "}
                and{" "}
                <DSLinkButton
                  href="https://www.scribenote.com/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  dataTestId="privacy-policy-link"
                >
                  Privacy Policy
                </DSLinkButton>
                .
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
